<template>
  <graphql-requester
    :gql="$options.GET_INSURANCES"
    :variables="variables"
    v-slot="{ data, loading }"
    @result="onResult"
  >
    <validation-provider
      class="col-span-1"
      tag="div"
      :name="fieldName"
      :rules="rules"
      mode="passive"
      v-slot="{ errors }"
    >
      <form-label :for="fieldName">
        Insurance Carrier Name
        <span v-if="isRequired" class="leading-none text-red-400" aria-hidden="true">*</span>
        <span v-if="isRequired" class="sr-only">(required)</span>
      </form-label>

      <div
        v-if="loading"
        class="h-10 animate-pulse"
        role="status"
        aria-label="Loading insurance carriers"
      >
        <div class="h-10 bg-gray-200 rounded"></div>
      </div>

      <template
        v-else-if="
          data &&
          data.getScheduleDetails &&
          data.getScheduleDetails.insurances &&
          !loading
        "
      >
        <form-search-select
          :id="fieldName"
          :disabled="!hasInsurances"
          :options="[
            ...data.getScheduleDetails.insurances,
            { id: 'other', name: 'Other Carrier' },
          ]"
          :controls="controls"
          v-model="internalValue"
          @input="onInput"
          :aria-required="isRequired"
          :aria-invalid="!!errors[0] || !!internalErrors"
          :aria-describedby="(errors[0] || internalErrors) ? 'insurance-error' : null"
          placeholder="Choose your insurance carrier"
        ></form-search-select>
      </template>

      <alert-warning
        class="h-10"
        v-else
        role="alert"
        aria-live="assertive"
      >
        Something went wrong
      </alert-warning>

      <div id="insurance-error">
        <form-alert-msg
          v-if="errors[0]"
          role="alert"
          aria-live="assertive"
        >
          {{ errors[0] }}
        </form-alert-msg>

        <template v-if="internalErrors">
          <form-alert-msg
            v-for="(error, index) in internalErrors"
            :key="index"
            role="alert"
            aria-live="assertive"
          >
            {{ error }}
          </form-alert-msg>
        </template>
      </div>
    </validation-provider>
  </graphql-requester>
</template>

<script>
import { GET_INSURANCES } from "@/graphql";
import GraphqlRequester from "@/components/GraphqlRequester";
import { FormLabel, FormAlertMsg, FormSearchSelect } from "@/components/Form/";
import { ValidationProvider, extend } from "vee-validate";
import AlertWarning from "@/components/Alert/AlertWarning";
import { required, max } from "vee-validate/dist/rules";
extend("required", { ...required, message: "This field is required." });
extend("max_insurance_name", {
  ...max,
  message: "This field may not be greater than 150 characters.",
});

export default {
  GET_INSURANCES,
  components: {
    ValidationProvider,
    GraphqlRequester,
    FormLabel,
    FormAlertMsg,
    FormSearchSelect,
    AlertWarning,
  },
  props: ["value", "http-errors", "controls"],
  data() {
    return {
      internalValue: null,
      insurances: [],
    };
  },
  computed: {
    isRequired() {
      return (
        this.$store.state.config?.sameday ||
        this.$store.state.settings.insurance_fields.carrier_required
      );
    },
    fieldName() {
      return this.$vnode.key;
    },
    rules() {
      return this.isRequired
        ? "required|max_insurance_name:150"
        : "max_insurance_name:150";
    },
    hasInsurances() {
      return this.insurances && this.insurances.length > 0;
    },
    internalErrors() {
      if (!this.httpErrors) return;
      return this.httpErrors.map((el) => el.replace(/input.insurance./i, ""));
    },
    locationId() {
      return (
        this.$store.getters?.locationId || this.controls?.locations?.location_id
      );
    },
    variables() {
      if (!this.locationId) return;
      let input = {};
      input.location_id = this.locationId;
      return { input };
    },
  },
  watch: {
    value: {
      handler(val) {
        this.internalValue = val;
      },
      immediate: true,
    },
  },
  methods: {
    onResult(data) {
      this.insurances = data?.getScheduleDetails?.insurances;
      if (this.insurances && this.insurances.length === 0) {
        this.internalValue = { id: "other", name: "Other" };
        this.$emit("input:field", this.internalValue);
      }
      // this.$emit('on:results', this.insurances);
      this.$store.commit("fieldUpdate", {
        value: this.insurances,
        key: "insurances",
      });
    },
    onInput(val) {
      this.$emit("input:field", val);
    },
  },
};
</script>

<style scoped>
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}
</style>